import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { kpisAPI, periodsAPI, resultsAPI, goalsAPI, commentsAPI } from '../lib/api';
import { useKPIOrder } from '../lib/useKPIOrder';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  ClipboardList,
  Calendar,
  CalendarDays,
  Target,
  Save,
  AlertCircle,
  CheckCircle2,
  Clock,
  Lock,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  Layers,
  GitBranch,
  Info,
  AlertTriangle,
  Zap,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, getBimesterName, formatNumber, isPeriodEditable, isPeriodClosed, isPeriodFuture, calculateKPIStatus, calculateRangeStatus, formatGoalRange, getStatusText, cn, getPeriodicityLabel } from '../lib/utils';

// Comment label configuration
const COMMENT_LABELS = {
  informativo: { label: 'Informativo', icon: Info, color: 'bg-blue-100 text-blue-700 border-blue-300' },
  desviacion: { label: 'Desviación', icon: AlertTriangle, color: 'bg-amber-100 text-amber-700 border-amber-300' },
  accion_tomada: { label: 'Plan de Acción', icon: Zap, color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
};

// Compact status indicator for capture nodes
const NodeStatusIndicator = ({ result, goal, evaluationType, goalRange = null }) => {
  const status = goalRange
    ? calculateRangeStatus(result, goalRange)
    : calculateKPIStatus(result, goal, evaluationType);
  
  if (status === 'pending') return null;
  
  const statusConfig = goalRange
    ? {
        success: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'En rango' },
        error: { bg: 'bg-rose-100', text: 'text-rose-700', label: 'Fuera de rango' },
      }
    : {
        success: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Cumplido' },
        error: { bg: 'bg-rose-100', text: 'text-rose-700', label: 'No cumplido' },
      };
  
  const config = statusConfig[status];
  
  return (
    <span className={cn(
      'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap',
      config.bg, config.text
    )}>
      {config.label}
    </span>
  );
};

const Capture = () => {
  const { user, isAdmin, isViewer: isViewerBase } = useAuth();
  const { canAccess: canAccessPerm } = usePermissions();
  // Fase 3: 'basic' permite capturar en KPIs propios/asignados; 'full' permite además capturar en KPIs de otros.
  // La restricción de propietario se enforza implícitamente por la visibilidad de KPIs y en backend.
  const canCaptureWrite = isAdmin() || canAccessPerm('kpis', 'captura');
  // Effective viewer mode: no tiene acceso en captura (none) o es viewer sin granular basic/full
  const isViewer = () => !canCaptureWrite;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [kpis, setKpis] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [goals, setGoals] = useState([]);
  const [existingResults, setExistingResults] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [captureData, setCaptureData] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [editReasonError, setEditReasonError] = useState('');
  const [expandedKPIs, setExpandedKPIs] = useState({});
  // New state for periodicity-based buckets
  const [kpiBuckets, setKpiBuckets] = useState({}); // { kpiId: { buckets: [], currentBucket: null } }
  const [selectedBuckets, setSelectedBuckets] = useState({}); // { kpiId: bucketId }
  // Capture context per KPI (pending period info)
  const [captureContexts, setCaptureContexts] = useState({}); // { kpiId: { has_pending, pending_period, ... } }
  // Comentarios: evidencia visible por fila { key: { count, last } } y diálogo de historial
  const [commentInfo, setCommentInfo] = useState({});
  const [historyDialog, setHistoryDialog] = useState({ open: false, loading: false, items: [], title: '' });
  // Confirmación de comentario sin guardar al cambiar de periodo
  const [pendingPeriodChange, setPendingPeriodChange] = useState(null);

  // Apply saved KPI order from KPIs page (single source of truth)
  const { orderedKpis: displayKpis } = useKPIOrder(kpis);

  useEffect(() => {
    fetchInitialData();
  }, [selectedYear]);

  useEffect(() => {
    if (selectedPeriod) {
      fetchResultsForPeriod();
    }
  }, [selectedPeriod]);

  // Fetch capture context for a weekly KPI (pending period detection)
  const fetchCaptureContext = useCallback(async (kpiId) => {
    // Check if already fetched
    if (captureContexts[kpiId]) {
      return captureContexts[kpiId];
    }
    
    try {
      const res = await periodsAPI.getCaptureContext(kpiId);
      const context = res.data;
      
      setCaptureContexts(prev => ({
        ...prev,
        [kpiId]: context
      }));
      
      // If there's a pending period, show a toast notification
      if (context.has_pending && context.message) {
        toast.warning(context.message, {
          duration: 6000,
          id: `pending-period-${kpiId}`, // Prevent duplicates
        });
      }
      
      return context;
    } catch (error) {
      console.error(`Error fetching capture context for KPI ${kpiId}:`, error);
      return null;
    }
  }, [captureContexts]);

  // Fetch buckets for a specific KPI when it's expanded
  const fetchBucketsForKPI = useCallback(async (kpi) => {
    if (!kpi || kpi.periodicity === 'weekly') {
      // For weekly KPIs, fetch capture context instead
      const context = await fetchCaptureContext(kpi.id);
      return context;
    }
    
    // Check if already fetched
    if (kpiBuckets[kpi.id]?.buckets?.length > 0) {
      return kpiBuckets[kpi.id].currentBucket;
    }
    
    try {
      const bucketsRes = await kpisAPI.getBuckets(kpi.id, selectedYear);
      const buckets = bucketsRes.data.buckets || [];
      const currentBucket = buckets.find(b => b.status === 'open') || buckets[0];
      
      setKpiBuckets(prev => ({
        ...prev,
        [kpi.id]: { buckets, currentBucket }
      }));
      
      // Set initial selected bucket to current if not already selected
      if (!selectedBuckets[kpi.id] && currentBucket) {
        setSelectedBuckets(prev => ({
          ...prev,
          [kpi.id]: currentBucket.bucket_id
        }));
      }
      
      // Return current bucket for immediate use
      return currentBucket;
    } catch (error) {
      console.error(`Error fetching buckets for KPI ${kpi.id}:`, error);
      return null;
    }
  }, [selectedYear, kpiBuckets, selectedBuckets, fetchCaptureContext]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [kpisRes, periodsRes, currentPeriodRes, goalsRes] = await Promise.all([
        kpisAPI.getAll({ is_active: true, view: 'capture' }),
        periodsAPI.getAll({ year: selectedYear }),
        periodsAPI.getCurrent(),
        goalsAPI.getAll({ year: selectedYear }),
      ]);
      
      setKpis(kpisRes.data);
      setPeriods(periodsRes.data);
      setGoals(goalsRes.data);
      
      if (currentPeriodRes.data) {
        setCurrentPeriod(currentPeriodRes.data);
        setSelectedPeriod(currentPeriodRes.data.id);
      } else if (periodsRes.data.length > 0) {
        setSelectedPeriod(periodsRes.data[0].id);
      }
      
      // Clear bucket and context cache when year changes
      setKpiBuckets({});
      setSelectedBuckets({});
      setCaptureContexts({});
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchResultsForPeriod = async () => {
    try {
      const resultsRes = await resultsAPI.getAll({ period_id: selectedPeriod });
      setExistingResults(resultsRes.data);
      
      // Initialize capture data with existing results
      // For hierarchical KPIs, key is `${kpi_id}_${node_id}`
      // For simple KPIs, key is just `kpi_id`
      const initialData = {};
      resultsRes.data.forEach((result) => {
        const key = result.node_id ? `${result.kpi_id}_${result.node_id}` : result.kpi_id;
        initialData[key] = {
          value: result.value,
          resultId: result.id,
          status: result.status,
          node_id: result.node_id,
          comment: '',  // Los comentarios se acumulan vía historial (commentsAPI); el campo inicia vacío
        };
      });
      setCaptureData(initialData);
    } catch (error) {
      console.error('Error fetching results:', error);
    }
  };
  
  // Fetch results for a specific bucket (for non-weekly KPIs)
  const fetchResultsForBucket = useCallback(async (kpiId, bucketId) => {
    try {
      // For non-weekly periodicities, we query results by bucket_id
      const resultsRes = await resultsAPI.getAll({ kpi_id: kpiId, bucket_id: bucketId });
      const results = resultsRes.data || [];
      
      // Update capture data for this KPI's bucket
      results.forEach((result) => {
        const key = result.node_id ? `${result.kpi_id}_${result.node_id}_${bucketId}` : `${result.kpi_id}_${bucketId}`;
        setCaptureData(prev => ({
          ...prev,
          [key]: {
            value: result.value,
            resultId: result.id,
            status: result.status,
            node_id: result.node_id,
            bucket_id: bucketId,
            comment: '',  // Los comentarios se acumulan vía historial (commentsAPI); el campo inicia vacío
          }
        }));
      });
    } catch (error) {
      console.error(`Error fetching results for bucket ${bucketId}:`, error);
    }
  }, []);

  const getGoalForKPI = (kpiId, periodId, nodeId = null, bucket = null) => {
    const isWeeklyCtx = !bucket || bucket.periodicity === 'weekly';

    // WEEKLY: emparejar por el goal cuyo per_goals CONTIENE este período (robusto para
    // agrupación bimestral/trimestral/mensual). Antes se emparejaba por bimestre calendario,
    // lo cual fallaba con trimestral/mensual (goal.bimester = índice de trimestre/mes, no
    // el bimestre del período) -> meta = 0 en los períodos desalineados.
    if (isWeeklyCtx) {
      let g = goals.find((gg) => gg.kpi_id === kpiId && gg.node_id === nodeId
        && (gg.per_goals || []).some((pg) => pg.period_id === periodId));
      if (!g) {
        // Fallback a goal sin node_id
        g = goals.find((gg) => gg.kpi_id === kpiId && !gg.node_id
          && (gg.per_goals || []).some((pg) => pg.period_id === periodId));
      }
      if (!g) return 0;
      const perGoal = g.per_goals.find((pg) => pg.period_id === periodId);
      return perGoal?.goal || 0;
    }

    // NON-WEEKLY (buckets): la meta se guarda por índice de grupo en el campo bimester.
    const bucketGoalPeriod = bucket ? (bucket.quarter || bucket.bucket_number) : null;
    const goal = goals.find((g) => {
      const matchKpi = g.kpi_id === kpiId;
      const matchNode = g.node_id === nodeId;
      const matchBimester = bucketGoalPeriod ? g.bimester === bucketGoalPeriod : true;
      return matchKpi && matchNode && matchBimester;
    });
    if (!goal) {
      const fallbackGoal = goals.find((g) => {
        const matchKpi = g.kpi_id === kpiId;
        const noNode = !g.node_id;
        const matchBimester = bucketGoalPeriod ? g.bimester === bucketGoalPeriod : true;
        return matchKpi && noNode && matchBimester;
      });
      if (!fallbackGoal) return 0;
      return fallbackGoal.bimester_goal || 0;
    }
    return goal.bimester_goal || 0;
  };

  // Devuelve el rango {min,max} configurado para un KPI simple tipo 'range' en el periodo/bucket actual
  const getGoalRangeForKPI = (kpiId, periodId, bucket = null) => {
    const isWeeklyCtx = !bucket || bucket.periodicity === 'weekly';
    let goal;
    if (isWeeklyCtx) {
      // Emparejar por per_goals que contenga el período (igual que getGoalForKPI)
      goal = goals.find((g) => g.kpi_id === kpiId && !g.node_id
        && (g.per_goals || []).some((pg) => pg.period_id === periodId));
    } else {
      const bucketGoalPeriod = bucket ? (bucket.quarter || bucket.bucket_number) : null;
      goal = goals.find((g) => g.kpi_id === kpiId && !g.node_id
        && (!bucketGoalPeriod || g.bimester === bucketGoalPeriod));
    }
    const gr = goal?.goal_range;
    if (gr && gr.min != null && gr.max != null) return gr;
    return null;
  };

  const handleValueChange = (key, value) => {
    setCaptureData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: value === '' ? '' : parseFloat(value) || 0,
      },
    }));
  };

  const handleCommentChange = (key, comment) => {
    setCaptureData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        comment: comment,
      },
    }));
  };

  const handleLabelChange = (key, label) => {
    setCaptureData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        commentLabel: label,
      },
    }));
  };

  // Refresca el indicador de comentarios (conteo + último) de una fila
  const refreshCommentInfo = async (kpiId, periodId, nodeId, key) => {
    try {
      const params = { kpi_id: kpiId, period_id: periodId };
      if (nodeId) params.node_id = nodeId;
      const res = await commentsAPI.getAll(params);
      const items = (res.data || []).filter((c) => c.estado !== 'cancelado');
      // GET /comments viene ordenado desc por created_at → el primero es el último
      setCommentInfo((prev) => ({
        ...prev,
        [key]: { count: items.length, last: items[0]?.text || '' },
      }));
    } catch (e) {
      // silencioso: el indicador es informativo
    }
  };

  // Abre el historial de comentarios de una fila en un diálogo ligero
  const openCommentHistory = async (kpiId, periodId, nodeId, title, key) => {
    setHistoryDialog({ open: true, loading: true, items: [], title });
    try {
      const params = { kpi_id: kpiId, period_id: periodId };
      if (nodeId) params.node_id = nodeId;
      const res = await commentsAPI.getAll(params);
      const items = res.data || [];
      // Sincroniza el indicador de la fila con el conteo real
      if (key) {
        const active = items.filter((c) => c.estado !== 'cancelado');
        setCommentInfo((prev) => ({ ...prev, [key]: { count: active.length, last: active[0]?.text || '' } }));
      }
      setHistoryDialog({ open: true, loading: false, items, title });
    } catch (e) {
      setHistoryDialog({ open: true, loading: false, items: [], title });
    }
  };

  // Indicador de evidencia: conteo de comentarios + acceso al historial
  const renderCommentIndicator = (key, kpiId, periodId, nodeId, title) => {
    if (isViewer()) return null;
    const info = commentInfo[key];
    return (
      <div className="pl-6 mt-1 flex items-center gap-2 text-xs" data-testid={`comment-info-${nodeId || kpiId}`}>
        {info?.count ? (
          <>
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
            <span className="text-emerald-600 font-medium">
              {info.count} comentario{info.count !== 1 ? 's' : ''} registrado{info.count !== 1 ? 's' : ''}
            </span>
            {info.last && <span className="text-slate-400 truncate max-w-[220px]">· "{info.last}"</span>}
          </>
        ) : (
          <span className="text-slate-400">Sin comentarios aún</span>
        )}
        <button
          type="button"
          className="text-blue-600 hover:underline flex-shrink-0"
          onClick={(e) => { e.stopPropagation(); openCommentHistory(kpiId, periodId, nodeId, title, key); }}
          data-testid={`view-comments-${nodeId || kpiId}`}
        >
          Ver historial
        </button>
      </div>
    );
  };

  // Core: guarda el comentario de una fila si hay texto. Devuelve true si guardó.
  const saveCommentCore = async (kpiId, periodId, nodeId, key) => {
    const comment = captureData[key]?.comment;
    const commentLabel = captureData[key]?.commentLabel || 'informativo';
    if (!comment || !comment.trim()) return false;
    await commentsAPI.create({
      kpi_id: kpiId,
      period_id: periodId,
      node_id: nodeId || null,
      text: comment.trim(),
      label: commentLabel,
      type: 'comment',
    });
    setCaptureData((prev) => ({
      ...prev,
      [key]: { ...prev[key], comment: '', commentLabel: 'informativo' },
    }));
    await refreshCommentInfo(kpiId, periodId, nodeId, key);
    return true;
  };

  // Save comment independently (botón "Guardar comentario" o Enter en el campo)
  const handleSaveCommentOnly = async (kpiId, periodId, nodeId = null, key) => {
    const comment = captureData[key]?.comment;
    if (!comment || !comment.trim()) {
      toast.error('Escribe un comentario');
      return;
    }
    setSaving(true);
    try {
      await saveCommentCore(kpiId, periodId, nodeId, key);
      toast.success('Comentario guardado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar comentario');
    } finally {
      setSaving(false);
    }
  };

  // Handle save for bucket-based KPIs (non-weekly)
  const handleSaveBucketResult = async (kpiId, bucketId, nodeId = null) => {
    const key = nodeId ? `${kpiId}_${nodeId}_${bucketId}` : `${kpiId}_${bucketId}`;
    const value = captureData[key]?.value;
    const comment = captureData[key]?.comment || null;
    const commentLabel = captureData[key]?.commentLabel || 'informativo';
    
    if (value === '' || value === undefined) {
      toast.error('Ingresa un valor');
      return;
    }
    
    // Get bucket to check if it's editable
    const kpiBucketData = kpiBuckets[kpiId];
    const bucket = kpiBucketData?.buckets?.find(b => b.bucket_id === bucketId);
    
    if (bucket?.status === 'future') {
      toast.error('No puedes capturar resultados en un bucket futuro');
      return;
    }

    setSaving(true);
    try {
      const existingResult = captureData[key]?.resultId;
      let valueOk = true;

      if (existingResult) {
        // Update existing result
        if (bucket?.status === 'closed') {
          setEditingResult({ kpiId, nodeId, bucketId, resultId: existingResult, value, key, comment, commentLabel, isBucket: true });
          setEditDialogOpen(true);
          setSaving(false);
          return;
        }
      }

      try {
        if (existingResult) {
          await resultsAPI.update(existingResult, { value: parseFloat(value) });
          toast.success('Resultado actualizado');
        } else {
          await resultsAPI.create({
            kpi_id: kpiId,
            bucket_id: bucketId,
            value: parseFloat(value),
            node_id: nodeId,
          });
          toast.success('Resultado guardado');
        }
      } catch (error) {
        valueOk = false;
        toast.error(error.response?.data?.detail || 'Error al guardar el valor');
      }

      // Guardar el comentario de forma INDEPENDIENTE (no se pierde si el valor falla)
      try {
        const bucketPeriodId = bucket?.period_ids?.[0] || selectedPeriod;
        const commentSaved = await saveCommentCore(kpiId, bucketPeriodId, nodeId, key);
        if (commentSaved) toast.success('Comentario guardado');
      } catch (e) {
        toast.error('El comentario no se pudo guardar');
      }

      if (valueOk) await fetchResultsForBucket(kpiId, bucketId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveResult = async (kpiId, nodeId = null) => {
    const key = nodeId ? `${kpiId}_${nodeId}` : kpiId;
    const value = captureData[key]?.value;
    const comment = captureData[key]?.comment || null;
    const commentLabel = captureData[key]?.commentLabel || 'informativo';
    
    if (value === '' || value === undefined) {
      toast.error('Ingresa un valor');
      return;
    }

    // Validate: cannot save to future period
    const period = periods.find((p) => p.id === selectedPeriod);
    if (isPeriodFuture(period)) {
      toast.error('No puedes capturar resultados en un periodo futuro');
      return;
    }

    setSaving(true);
    try {
      const existingResult = captureData[key]?.resultId;
      let valueOk = true;

      if (existingResult) {
        // Period closed - any user must provide a reason (traceability)
        if (isPeriodClosed(period)) {
          setEditingResult({ kpiId, nodeId, resultId: existingResult, value, key, comment, commentLabel });
          setEditDialogOpen(true);
          setSaving(false);
          return;
        }
      }

      try {
        if (existingResult) {
          await resultsAPI.update(existingResult, { value: parseFloat(value) });
          toast.success('Resultado actualizado');
        } else {
          await resultsAPI.create({
            kpi_id: kpiId,
            period_id: selectedPeriod,
            value: parseFloat(value),
            node_id: nodeId,
          });
          toast.success('Resultado guardado');
        }
      } catch (error) {
        valueOk = false;
        toast.error(error.response?.data?.detail || 'Error al guardar el valor');
      }

      // Guardar el comentario de forma INDEPENDIENTE (no se pierde si el valor falla)
      try {
        const commentSaved = await saveCommentCore(kpiId, selectedPeriod, nodeId, key);
        if (commentSaved) toast.success('Comentario guardado');
      } catch (e) {
        toast.error('El comentario no se pudo guardar');
      }

      if (valueOk) fetchResultsForPeriod();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleEditWithReason = async () => {
    // Validate reason - minimum 5 characters, not just whitespace
    const trimmedReason = editReason.trim();
    if (!trimmedReason) {
      setEditReasonError('El motivo es obligatorio');
      return;
    }
    if (trimmedReason.length < 5) {
      setEditReasonError('El motivo debe tener al menos 5 caracteres');
      return;
    }

    setEditReasonError('');
    setSaving(true);
    try {
      await resultsAPI.update(editingResult.resultId, {
        value: parseFloat(editingResult.value),
        reason: trimmedReason,
      });
      toast.success('Resultado actualizado con auditoría');
      // Unificar: guardar también el comentario si el usuario escribió uno
      if (editingResult?.key) {
        let periodId = selectedPeriod;
        if (editingResult.isBucket && editingResult.bucketId) {
          const kb = kpiBuckets[editingResult.kpiId];
          const b = kb?.buckets?.find((x) => x.bucket_id === editingResult.bucketId);
          periodId = b?.period_ids?.[0] || selectedPeriod;
        }
        try {
          const saved = await saveCommentCore(editingResult.kpiId, periodId, editingResult.nodeId, editingResult.key);
          if (saved) toast.success('Comentario guardado');
        } catch (e) { /* no bloquear el cierre por el comentario */ }
      }
      setEditDialogOpen(false);
      setEditReason('');
      setEditReasonError('');
      setEditingResult(null);
      fetchResultsForPeriod();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al actualizar';
      // Check if error is about missing reason
      if (errorMessage.toLowerCase().includes('motivo')) {
        setEditReasonError(errorMessage);
      } else {
        toast.error(errorMessage);
      }
      // Keep dialog open on error - don't lose the reason text
    } finally {
      setSaving(false);
    }
  };

  // Handle dialog close - only allow if not saving
  const handleEditDialogClose = (open) => {
    if (!open && saving) return; // Prevent closing while saving
    if (!open) {
      setEditReason('');
      setEditReasonError('');
      setEditingResult(null);
    }
    setEditDialogOpen(open);
  };

  // Validate reason for button enable state
  const isReasonValid = editReason.trim().length >= 5;

  // Cambio de periodo con confirmación si hay comentario sin guardar
  const hasUnsavedComment = () =>
    Object.values(captureData).some((d) => d?.comment && d.comment.trim());

  const handlePeriodChange = (newPeriodId) => {
    if (newPeriodId === selectedPeriod) return;
    if (hasUnsavedComment()) {
      setPendingPeriodChange(newPeriodId);
      return;
    }
    setSelectedPeriod(newPeriodId);
  };

  const confirmPeriodChange = () => {
    if (pendingPeriodChange) setSelectedPeriod(pendingPeriodChange);
    setPendingPeriodChange(null);
  };

  const selectedPeriodData = periods.find((p) => p.id === selectedPeriod);
  const isEditable = isPeriodEditable(selectedPeriodData, isAdmin());
  const periodClosed = isPeriodClosed(selectedPeriodData);
  const periodFuture = isPeriodFuture(selectedPeriodData);

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-24" />
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  // Count KPIs by periodicity for UI
  const weeklyKpis = kpis.filter(k => !k.periodicity || k.periodicity === 'weekly');
  const nonWeeklyKpis = kpis.filter(k => k.periodicity && k.periodicity !== 'weekly');
  const hasWeeklyKpis = weeklyKpis.length > 0;
  const hasNonWeeklyKpis = nonWeeklyKpis.length > 0;

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6" data-testid="capture-page">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">Captura de Resultados</h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">Registra los resultados de tus KPIs según su periodicidad</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-28 sm:w-32" data-testid="capture-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2026">2026</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
              </SelectContent>
            </Select>
            {/* KPI counts by periodicity */}
            {hasWeeklyKpis && (
              <Badge variant="outline" className="text-xs bg-slate-50">
                <Calendar className="w-3 h-3 mr-1" />
                {weeklyKpis.length} Semanales
              </Badge>
            )}
            {hasNonWeeklyKpis && (
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                <CalendarDays className="w-3 h-3 mr-1" />
                {nonWeeklyKpis.length} Otros periodos
              </Badge>
            )}
          </div>
        </div>

        {/* Period Selector - ONLY for Weekly KPIs */}
        {hasWeeklyKpis && (
        <Card data-testid="period-selector-card">
          <CardContent className="py-3 sm:py-4 px-3 sm:px-6">
            <div className="flex flex-col gap-3 sm:gap-4">
              <div className="flex items-start sm:items-center gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm text-slate-500">
                    Periodo seleccionado 
                    <span className="text-slate-400 ml-1">(para {weeklyKpis.length} KPI{weeklyKpis.length !== 1 ? 's' : ''} semanal{weeklyKpis.length !== 1 ? 'es' : ''})</span>
                  </p>
                  <Select value={selectedPeriod || ''} onValueChange={handlePeriodChange}>
                    <SelectTrigger className="w-full sm:w-80 mt-1" data-testid="period-select">
                      <SelectValue placeholder="Selecciona un periodo" />
                    </SelectTrigger>
                    <SelectContent>
                      {periods.map((period) => {
                        const isFuture = isPeriodFuture(period);
                        return (
                          <SelectItem key={period.id} value={period.id}>
                            <span className={isFuture ? 'text-slate-400' : ''}>
                              PER {period.number} ({formatDate(period.start_date)} - {formatDate(period.end_date)})
                              {isFuture && ' 🔒'}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {selectedPeriodData && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 sm:ml-auto pl-12 sm:pl-0">
                  <div className="sm:text-right">
                    <p className="text-xs sm:text-sm text-slate-500">
                      <span className="font-medium text-slate-700">
                        {formatDate(selectedPeriodData.start_date)} - {formatDate(selectedPeriodData.end_date)}
                      </span>
                      {selectedPeriodData.is_special && <span className="ml-1 text-amber-600">(Especial)</span>}
                    </p>
                    <p className="text-xs text-slate-400">Edición hasta: {formatDate(selectedPeriodData.edit_deadline)}</p>
                  </div>
                  {/* Period status badge */}
                  {periodFuture ? (
                    <Badge variant="secondary" className="w-fit bg-blue-50 text-blue-700 border-blue-200">
                      <Lock className="w-3 h-3 mr-1" />
                      Periodo futuro
                    </Badge>
                  ) : periodClosed ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="w-fit bg-amber-50 text-amber-700 border-amber-200">
                        <Lock className="w-3 h-3 mr-1" />
                        Cerrado
                      </Badge>
                      {isAdmin() && (
                        <Badge variant="outline" className="w-fit text-xs bg-slate-50 text-slate-600 border-slate-300">
                          Admin: puede editar
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <Badge variant="default" className="w-fit bg-emerald-50 text-emerald-700">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Editable
                    </Badge>
                  )}
                </div>
              )}
              
              {/* Future period warning message */}
              {periodFuture && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg ml-12 sm:ml-0">
                  <Lock className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <p className="text-sm text-blue-700">
                    No puedes capturar resultados hasta que inicie el periodo. 
                    <span className="font-medium"> Fecha de inicio: {formatDate(selectedPeriodData.start_date)}</span>
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Info banner for non-weekly KPIs */}
        {hasNonWeeklyKpis && !hasWeeklyKpis && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <CalendarDays className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <p className="text-sm text-blue-700">
              Tus KPIs tienen periodicidad no semanal. Expande cada KPI para ver su selector de periodo específico.
            </p>
          </div>
        )}

        {/* KPIs Capture List */}
        {periods.length === 0 && !hasNonWeeklyKpis ? (
          <Card className="py-12">
            <div className="text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No hay periodos configurados</p>
              <p className="text-slate-400 text-sm mt-1">
                {isAdmin() ? 'Genera los periodos desde Configuración' : 'Contacta al administrador'}
              </p>
            </div>
          </Card>
        ) : kpis.length === 0 ? (
          <Card className="py-12">
            <div className="text-center">
              <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No hay KPIs activos</p>
              <p className="text-slate-400 text-sm mt-1">
                Contacta al administrador para configurar indicadores
              </p>
            </div>
          </Card>
        ) : (
          <Card data-testid="capture-list-card">
            <CardHeader className="border-b border-slate-100 px-3 sm:px-6">
              <CardTitle className="text-base sm:text-lg font-['Manrope'] flex items-center gap-2">
                <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5" />
                KPIs para captura
                {isViewer() && (
                  <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-700 border-amber-200">
                    <Lock className="w-3 h-3 mr-1" />
                    Solo lectura
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {/* Read-only warning banner */}
              {isViewer() && (
                <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 text-sm text-amber-800">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    <span>{isViewerBase() ? (<>Tu rol de <strong>Viewer</strong> solo permite visualizar datos. No puedes capturar resultados.</>) : (<>Solo lectura: no tienes permiso para capturar resultados en este módulo.</>)}</span>
                  </div>
                </div>
              )}
              <div className="divide-y divide-slate-100">
                {displayKpis.map((kpi) => {
                  const hasHierarchy = kpi.has_hierarchy && kpi.hierarchy_nodes?.length > 0;
                  const isExpanded = expandedKPIs[kpi.id];
                  const leafNodes = hasHierarchy ? kpi.hierarchy_nodes.filter(n => n.is_leaf) : [];
                  
                  // Check periodicity - default to weekly if not set
                  const isWeekly = !kpi.periodicity || kpi.periodicity === 'weekly';
                  const periodicityLabel = getPeriodicityLabel(kpi.periodicity || 'weekly');
                  
                  // Get bucket data for non-weekly KPIs
                  const kpiBucketData = kpiBuckets[kpi.id];
                  const selectedBucketId = selectedBuckets[kpi.id];
                  const selectedBucket = kpiBucketData?.buckets?.find(b => b.bucket_id === selectedBucketId);
                  
                  // Determine bucket status for non-weekly KPIs
                  const bucketFuture = !isWeekly && selectedBucket?.status === 'future';
                  const bucketClosed = !isWeekly && selectedBucket?.status === 'closed';
                  const bucketOpen = !isWeekly && selectedBucket?.status === 'open';
                  
                  // SECURITY: Block capture for Viewer role AND future periods/buckets
                  const canCapture = kpi.can_capture && !isViewer() && (isWeekly ? !periodFuture : !bucketFuture);
                  const isEditableForKPI = isWeekly ? isEditable : (bucketOpen || (bucketClosed && isAdmin()));
                  
                  // For simple KPIs, use kpi.id as key (weekly) or kpi.id_bucket (non-weekly)
                  const simpleKey = isWeekly ? kpi.id : (selectedBucketId ? `${kpi.id}_${selectedBucketId}` : kpi.id);
                  const goal = hasHierarchy ? 0 : getGoalForKPI(kpi.id, selectedPeriod, null, selectedBucket);
                  const isRangeKPI = kpi.goal_calculation_type === 'range';
                  const kpiGoalRange = (!hasHierarchy && isRangeKPI) ? getGoalRangeForKPI(kpi.id, selectedPeriod, selectedBucket) : null;
                  const currentValue = hasHierarchy ? null : captureData[simpleKey]?.value;
                  const hasExistingResult = hasHierarchy ? false : !!captureData[simpleKey]?.resultId;
                  
                  // Calculate total for hierarchical KPIs
                  let hierarchyTotal = 0;
                  let hierarchyCapturedCount = 0;
                  if (hasHierarchy) {
                    leafNodes.forEach(node => {
                      const nodeKey = isWeekly ? `${kpi.id}_${node.id}` : `${kpi.id}_${node.id}_${selectedBucketId}`;
                      if (captureData[nodeKey]?.resultId) {
                        hierarchyTotal += captureData[nodeKey]?.value || 0;
                        hierarchyCapturedCount++;
                      }
                    });
                  }
                  
                  // Unified: both simple and hierarchical KPIs are expandable
                  const totalNodes = hasHierarchy ? leafNodes.length : 1;
                  const capturedNodes = hasHierarchy ? hierarchyCapturedCount : (hasExistingResult ? 1 : 0);
                  
                  // Handle KPI expand - fetch buckets for non-weekly, capture context for weekly
                  const handleKPIExpand = async () => {
                    const newExpandedState = !isExpanded;
                    setExpandedKPIs({...expandedKPIs, [kpi.id]: newExpandedState});
                    
                    if (newExpandedState) {
                      if (!isWeekly) {
                        // Fetch buckets when expanding non-weekly KPI
                        const currentBucket = await fetchBucketsForKPI(kpi);
                        // Fetch results for current bucket
                        if (currentBucket) {
                          await fetchResultsForBucket(kpi.id, currentBucket.bucket_id);
                        }
                      } else {
                        // For weekly KPIs, fetch capture context to detect pending period
                        await fetchCaptureContext(kpi.id);
                      }
                    }
                  };
                  
                  // Handle bucket selection change
                  const handleBucketChange = async (bucketId) => {
                    setSelectedBuckets(prev => ({...prev, [kpi.id]: bucketId}));
                    await fetchResultsForBucket(kpi.id, bucketId);
                  };
                  
                  return (
                    <div 
                      key={kpi.id} 
                      className={cn(
                        "p-3 sm:p-4 transition-colors",
                        canCapture ? "hover:bg-slate-50" : "bg-slate-50/50"
                      )} 
                      data-testid={`capture-row-${kpi.id}`}
                    >
                      {/* KPI Header - Unified: Always clickable to expand */}
                      <div 
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={handleKPIExpand}
                      >
                        <div className={cn(
                          "w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                          hasHierarchy ? "bg-violet-100" : canCapture ? "bg-slate-100" : "bg-slate-200"
                        )}>
                          {hasHierarchy ? (
                            <GitBranch className="w-4 h-4 sm:w-5 sm:h-5 text-violet-600" />
                          ) : (
                            <Target className={cn("w-4 h-4 sm:w-5 sm:h-5", canCapture ? "text-slate-600" : "text-slate-400")} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Unified: Always show chevron */}
                            {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                            <h3 className={cn("font-medium text-sm sm:text-base truncate uppercase", canCapture ? "text-slate-900" : "text-slate-500")}>{kpi.name}</h3>
                            {/* Periodicity badge for non-weekly */}
                            {!isWeekly && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                <CalendarDays className="w-3 h-3 mr-1" />
                                {periodicityLabel}
                              </Badge>
                            )}
                            {!canCapture && (
                              <Badge variant="outline" className="text-xs bg-slate-100 text-slate-500 border-slate-300">
                                Solo lectura
                              </Badge>
                            )}
                            {hasHierarchy && (
                              <Badge variant="outline" className="text-xs hidden sm:inline-flex">
                                {kpi.hierarchy_dimension}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-slate-500 uppercase">{kpi.area}</p>
                        </div>
                        
                        {/* Unified Status Badge */}
                        <Badge 
                          variant="outline" 
                          className={cn(
                            "text-xs flex-shrink-0",
                            capturedNodes === totalNodes 
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                              : "bg-amber-50 text-amber-700 border-amber-200"
                          )}
                        >
                          {capturedNodes}/{totalNodes}
                        </Badge>
                      </div>
                      
                      {/* Unified Capture Panel - Same layout for both simple and hierarchical */}
                      {isExpanded && (
                        <div className={cn(
                          "mt-3 pl-4 sm:pl-10 space-y-2 border-l-2",
                          hasHierarchy ? "border-violet-200" : "border-slate-200"
                        )}>
                          {/* Bucket Selector for non-weekly KPIs */}
                          {!isWeekly && (
                            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                <div className="flex items-center gap-2">
                                  <CalendarDays className="w-4 h-4 text-blue-600" />
                                  <span className="text-sm font-medium text-blue-800">
                                    Selecciona {periodicityLabel.toLowerCase()}:
                                  </span>
                                </div>
                                {kpiBucketData?.buckets?.length > 0 ? (
                                  <div className="flex-1 flex items-center gap-2">
                                    <Select 
                                      value={selectedBucketId || ''} 
                                      onValueChange={handleBucketChange}
                                    >
                                      <SelectTrigger className="w-full sm:w-64 bg-white" data-testid={`bucket-select-${kpi.id}`}>
                                        <SelectValue placeholder={`Selecciona ${periodicityLabel.toLowerCase()}`} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {kpiBucketData.buckets.map((bucket) => {
                                          const isFuture = bucket.status === 'future';
                                          const isCurrent = bucket.status === 'open';
                                          return (
                                            <SelectItem key={bucket.bucket_id} value={bucket.bucket_id}>
                                              <span className={cn(isFuture && 'text-slate-400')}>
                                                {bucket.bucket_label}
                                                {isFuture && ' 🔒'}
                                                {isCurrent && ' ✓'}
                                              </span>
                                            </SelectItem>
                                          );
                                        })}
                                      </SelectContent>
                                    </Select>
                                    {/* Bucket status indicator */}
                                    {selectedBucket && (
                                      <>
                                        {bucketFuture && (
                                          <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-300">
                                            <Lock className="w-3 h-3 mr-1" />
                                            Futuro
                                          </Badge>
                                        )}
                                        {bucketClosed && (
                                          <Badge variant="secondary" className="bg-amber-100 text-amber-700 border-amber-300">
                                            <Lock className="w-3 h-3 mr-1" />
                                            Cerrado
                                          </Badge>
                                        )}
                                        {bucketOpen && (
                                          <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 border-emerald-300">
                                            <CheckCircle2 className="w-3 h-3 mr-1" />
                                            Activo
                                          </Badge>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-sm text-blue-600">Cargando buckets...</span>
                                )}
                              </div>
                              {/* Bucket dates info */}
                              {selectedBucket && (
                                <p className="text-xs text-blue-600 mt-2">
                                  {formatDate(selectedBucket.start_date)} - {formatDate(selectedBucket.end_date)}
                                  {selectedBucket.edit_deadline && (
                                    <span className="ml-2 text-blue-500">
                                      (Edición hasta: {formatDate(selectedBucket.edit_deadline)})
                                    </span>
                                  )}
                                </p>
                              )}
                              {/* Future bucket warning */}
                              {bucketFuture && (
                                <div className="flex items-center gap-2 mt-2 p-2 bg-blue-100 rounded text-sm text-blue-700">
                                  <Lock className="w-4 h-4" />
                                  <span>No puedes capturar hasta: {formatDate(selectedBucket?.start_date)}</span>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Show calculated total for hierarchical KPIs */}
                          {hasHierarchy && (
                            <div className="flex items-center justify-between px-2 py-1 bg-slate-50 rounded text-sm">
                              <span className="font-medium text-slate-600">Total calculado:</span>
                              <span className="font-bold">{formatNumber(hierarchyTotal)}</span>
                            </div>
                          )}
                          
                          {/* Unified capture rows */}
                          {hasHierarchy ? (
                            // Hierarchical: Render leaf nodes
                            leafNodes.map((node) => {
                              const nodeKey = isWeekly ? `${kpi.id}_${node.id}` : `${kpi.id}_${node.id}_${selectedBucketId}`;
                              const nodeGoal = getGoalForKPI(kpi.id, selectedPeriod, node.id, selectedBucket);
                              const nodeValue = captureData[nodeKey]?.value;
                              const nodeHasResult = !!captureData[nodeKey]?.resultId;
                              const parentNode = kpi.hierarchy_nodes.find(n => n.id === node.parent_id);
                              const nodeResultValue = nodeValue !== '' && nodeValue !== undefined ? parseFloat(nodeValue) : null;
                              
                              // Handler for saving result - use bucket or period based on KPI type
                              const handleNodeSave = (e) => {
                                e.stopPropagation();
                                if (isWeekly) {
                                  handleSaveResult(kpi.id, node.id);
                                } else {
                                  handleSaveBucketResult(kpi.id, selectedBucketId, node.id);
                                }
                              };
                              
                              return (
                                <div key={node.id}>
                                  <div 
                                    className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200"
                                    data-testid={`capture-node-${node.id}`}
                                  >
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <div className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0" />
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-medium truncate">{node.name}</span>
                                          {/* Node status indicator */}
                                          <NodeStatusIndicator 
                                            result={nodeResultValue} 
                                            goal={nodeGoal} 
                                            evaluationType={kpi.evaluation_type}
                                          />
                                        </div>
                                        {parentNode && (
                                          <span className="text-xs text-slate-400">{parentNode.name}</span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-center flex-shrink-0 hidden sm:block">
                                      <p className="text-xs text-slate-500">Meta</p>
                                      <p className="text-sm font-medium">{formatNumber(nodeGoal)}</p>
                                    </div>
                                    <div className="w-20 sm:w-24 flex-shrink-0">
                                      <Input
                                        type="number"
                                        step="any"
                                        value={nodeValue ?? ''}
                                        onChange={(e) => handleValueChange(nodeKey, e.target.value)}
                                        className="h-8 text-sm"
                                        placeholder="0"
                                        disabled={!canCapture || (!isEditableForKPI && !isAdmin())}
                                      />
                                    </div>
                                    {canCapture && (
                                      <Button
                                        onClick={handleNodeSave}
                                        disabled={saving || (!isEditableForKPI && !isAdmin())}
                                        size="sm"
                                        className="h-8 w-8 p-0 flex-shrink-0"
                                        data-testid={`save-result-${node.id}`}
                                      >
                                        <Save className="w-3 h-3" />
                                      </Button>
                                    )}
                                    {nodeHasResult && (
                                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                    )}
                                  </div>
                                  {/* Comment field with label selector for hierarchical node */}
                                  <div className="mt-1 mb-2 pl-4">
                                    <div className="flex items-start gap-2">
                                      <MessageSquare className="w-4 h-4 text-slate-400 mt-1.5 flex-shrink-0" />
                                      <div className="flex-1 flex flex-col sm:flex-row gap-2">
                                        <Select 
                                          value={captureData[nodeKey]?.commentLabel || 'informativo'} 
                                          onValueChange={(v) => handleLabelChange(nodeKey, v)}
                                          disabled={isViewer()}
                                        >
                                          <SelectTrigger className="w-full sm:w-36 h-8 text-xs">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {Object.entries(COMMENT_LABELS).map(([key, config]) => {
                                              const LabelIcon = config.icon;
                                              return (
                                                <SelectItem key={key} value={key}>
                                                  <span className="flex items-center gap-1.5">
                                                    <LabelIcon className="w-3 h-3" />
                                                    {config.label}
                                                  </span>
                                                </SelectItem>
                                              );
                                            })}
                                          </SelectContent>
                                        </Select>
                                        <Input
                                          type="text"
                                          value={captureData[nodeKey]?.comment || ''}
                                          onChange={(e) => handleCommentChange(nodeKey, e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                              e.preventDefault();
                                              handleSaveCommentOnly(kpi.id, isWeekly ? selectedPeriod : (selectedBucket?.period_ids?.[0] || selectedPeriod), node.id, nodeKey);
                                            }
                                          }}
                                          className="h-8 text-sm flex-1"
                                          placeholder="Comentario (opcional) — Enter para guardar"
                                          disabled={isViewer()}
                                          data-testid={`comment-input-${node.id}`}
                                        />
                                        {!isViewer() && (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8 flex-shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                            disabled={saving || !captureData[nodeKey]?.comment?.trim()}
                                            onClick={(e) => { e.stopPropagation(); handleSaveCommentOnly(kpi.id, isWeekly ? selectedPeriod : (selectedBucket?.period_ids?.[0] || selectedPeriod), node.id, nodeKey); }}
                                            data-testid={`save-comment-${node.id}`}
                                          >
                                            <Send className="w-3.5 h-3.5 mr-1.5" />
                                            Guardar comentario
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                    {renderCommentIndicator(nodeKey, kpi.id, isWeekly ? selectedPeriod : (selectedBucket?.period_ids?.[0] || selectedPeriod), node.id, `${kpi.name} — ${node.name}`)}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            // Simple KPI: Render single "Total" row with same layout
                            <div>
                              <div 
                                className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200"
                                data-testid={`capture-simple-${kpi.id}`}
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <div className="w-2 h-2 bg-slate-500 rounded-full flex-shrink-0" />
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium truncate">Total</span>
                                      {/* Simple KPI status indicator */}
                                      <NodeStatusIndicator 
                                        result={currentValue !== '' && currentValue !== undefined ? parseFloat(currentValue) : null} 
                                        goal={goal} 
                                        evaluationType={kpi.evaluation_type}
                                        goalRange={kpiGoalRange}
                                      />
                                    </div>
                                    <span className="text-xs text-slate-400">Valor único</span>
                                  </div>
                                </div>
                                <div className="text-center flex-shrink-0 hidden sm:block">
                                  <p className="text-xs text-slate-500">{isRangeKPI ? 'Rango' : 'Meta'}</p>
                                  <p className="text-sm font-medium">{isRangeKPI ? formatGoalRange(kpiGoalRange) : formatNumber(goal)}</p>
                                </div>
                                <div className="w-20 sm:w-24 flex-shrink-0">
                                  <Input
                                    type="number"
                                    step="any"
                                    value={currentValue ?? ''}
                                    onChange={(e) => handleValueChange(simpleKey, e.target.value)}
                                    className="h-8 text-sm"
                                    placeholder="0"
                                    disabled={!canCapture || (!isEditableForKPI && !isAdmin())}
                                    data-testid={`capture-input-${kpi.id}`}
                                  />
                                </div>
                                {canCapture && (
                                  <Button
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      if (isWeekly) {
                                        handleSaveResult(kpi.id);
                                      } else {
                                        handleSaveBucketResult(kpi.id, selectedBucketId);
                                      }
                                    }}
                                    disabled={saving || (!isEditableForKPI && !isAdmin())}
                                    size="sm"
                                    className="h-8 w-8 p-0 flex-shrink-0"
                                    data-testid={`save-result-${kpi.id}`}
                                  >
                                    <Save className="w-3 h-3" />
                                  </Button>
                                )}
                                {hasExistingResult && (
                                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                )}
                              </div>
                              {/* Comment field with label selector for simple KPIs */}
                              <div className="mt-2 pl-4">
                                <div className="flex items-start gap-2">
                                  <MessageSquare className="w-4 h-4 text-slate-400 mt-1.5 flex-shrink-0" />
                                  <div className="flex-1 flex flex-col sm:flex-row gap-2">
                                    <Select 
                                      value={captureData[simpleKey]?.commentLabel || 'informativo'} 
                                      onValueChange={(v) => handleLabelChange(simpleKey, v)}
                                      disabled={isViewer()}
                                    >
                                      <SelectTrigger className="w-full sm:w-36 h-8 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {Object.entries(COMMENT_LABELS).map(([key, config]) => {
                                          const LabelIcon = config.icon;
                                          return (
                                            <SelectItem key={key} value={key}>
                                              <span className="flex items-center gap-1.5">
                                                <LabelIcon className="w-3 h-3" />
                                                {config.label}
                                              </span>
                                            </SelectItem>
                                          );
                                        })}
                                      </SelectContent>
                                    </Select>
                                    <Input
                                      type="text"
                                      value={captureData[simpleKey]?.comment || ''}
                                      onChange={(e) => handleCommentChange(simpleKey, e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                          e.preventDefault();
                                          handleSaveCommentOnly(kpi.id, isWeekly ? selectedPeriod : (selectedBucket?.period_ids?.[0] || selectedPeriod), null, simpleKey);
                                        }
                                      }}
                                      className="h-8 text-sm flex-1"
                                      placeholder="Comentario (opcional) — Enter para guardar"
                                      disabled={isViewer()}
                                      data-testid={`comment-input-${kpi.id}`}
                                    />
                                    {!isViewer() && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-8 flex-shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                        disabled={saving || !captureData[simpleKey]?.comment?.trim()}
                                        onClick={(e) => { e.stopPropagation(); handleSaveCommentOnly(kpi.id, isWeekly ? selectedPeriod : (selectedBucket?.period_ids?.[0] || selectedPeriod), null, simpleKey); }}
                                        data-testid={`save-comment-${kpi.id}`}
                                      >
                                        <Send className="w-3.5 h-3.5 mr-1.5" />
                                        Guardar comentario
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                {renderCommentIndicator(simpleKey, kpi.id, isWeekly ? selectedPeriod : (selectedBucket?.period_ids?.[0] || selectedPeriod), null, kpi.name)}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit with Reason Dialog - For closed period results */}
        <Dialog open={editDialogOpen} onOpenChange={handleEditDialogClose}>
          <DialogContent 
            data-testid="edit-reason-dialog"
            onPointerDownOutside={(e) => saving && e.preventDefault()}
            onEscapeKeyDown={(e) => saving && e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle className="font-['Manrope'] flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Edición de periodo anterior
              </DialogTitle>
              <DialogDescription>
                Este periodo ya cerró su ventana de edición normal. Para mantener la trazabilidad, debes proporcionar un motivo del cambio.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Warning banner */}
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                <div className="flex items-start gap-2">
                  <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Estás modificando un resultado de un periodo anterior</p>
                    <p className="text-amber-700 mt-1">Esta acción quedará registrada en el log de auditoría con tu usuario, la fecha/hora y el motivo que proporciones.</p>
                  </div>
                </div>
              </div>

              {/* Edit summary */}
              {editingResult && (
                <div className="bg-slate-50 rounded-md p-3 text-sm space-y-1">
                  <p className="text-slate-600">
                    <span className="font-medium">KPI:</span> {kpis.find(k => k.id === editingResult.kpiId)?.name || 'N/A'}
                  </p>
                  <p className="text-slate-600">
                    <span className="font-medium">Periodo:</span> PER {selectedPeriodData?.number || 'N/A'}
                  </p>
                  <p className="text-slate-600">
                    <span className="font-medium">Nuevo valor:</span> {editingResult.value}
                  </p>
                </div>
              )}
              
              {/* Reason field */}
              <div className="space-y-2">
                <Label htmlFor="edit-reason" className="flex items-center gap-1">
                  Motivo de la edición
                  <span className="text-rose-500">*</span>
                </Label>
                <Textarea
                  id="edit-reason"
                  value={editReason}
                  onChange={(e) => {
                    setEditReason(e.target.value);
                    if (editReasonError) setEditReasonError(''); // Clear error on type
                  }}
                  placeholder="Ej: Corrección de error de captura, dato incorrecto ingresado por operador..."
                  className={cn(
                    "resize-none min-h-[80px]",
                    editReasonError && "border-rose-500 focus-visible:ring-rose-500"
                  )}
                  data-testid="edit-reason-input"
                />
                {editReasonError ? (
                  <p className="text-sm text-rose-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {editReasonError}
                  </p>
                ) : (
                  <p className="text-xs text-slate-400">
                    Mínimo 5 caracteres ({editReason.trim().length}/5)
                  </p>
                )}
              </div>
            </div>
            
            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => handleEditDialogClose(false)}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleEditWithReason} 
                disabled={saving || !isReasonValid} 
                data-testid="confirm-edit-btn"
                className={cn(!isReasonValid && "opacity-50 cursor-not-allowed")}
              >
                {saving ? 'Guardando...' : 'Confirmar edición'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirmación: comentario sin guardar al cambiar de periodo */}
        <Dialog open={!!pendingPeriodChange} onOpenChange={(o) => { if (!o) setPendingPeriodChange(null); }}>
          <DialogContent data-testid="unsaved-comment-dialog">
            <DialogHeader>
              <DialogTitle>Comentario sin guardar</DialogTitle>
              <DialogDescription>
                Tienes un comentario escrito que no has guardado. Si cambias de periodo se perderá. ¿Deseas continuar sin guardarlo?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPendingPeriodChange(null)} data-testid="unsaved-comment-cancel">
                Cancelar
              </Button>
              <Button variant="destructive" onClick={confirmPeriodChange} data-testid="unsaved-comment-continue">
                Continuar sin guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Historial de comentarios de una fila */}
        <Dialog open={historyDialog.open} onOpenChange={(o) => { if (!o) setHistoryDialog({ open: false, loading: false, items: [], title: '' }); }}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="comment-history-dialog">
            <DialogHeader>
              <DialogTitle>Historial de comentarios</DialogTitle>
              <DialogDescription>{historyDialog.title}</DialogDescription>
            </DialogHeader>
            {historyDialog.loading ? (
              <p className="text-sm text-slate-500 py-6 text-center">Cargando…</p>
            ) : historyDialog.items.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Aún no hay comentarios en este periodo.</p>
            ) : (
              <div className="space-y-2">
                {historyDialog.items.map((c) => (
                  <div key={c.id} className="border rounded-md p-3 text-sm" data-testid={`history-item-${c.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-xs font-medium text-slate-500">{c.created_by_name || 'Usuario'}</span>
                      <span className="text-[11px] text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                    </div>
                    <p className={cn('text-slate-700', c.estado === 'cancelado' && 'line-through text-slate-400')}>{c.text}</p>
                    {c.estado === 'cancelado' && <span className="text-[11px] text-rose-500">Anulado</span>}
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Capture;
